/**
 * DocumentFilters Component
 *
 * Filter controls for the documents list including:
 * - Patient picker
 * - Document type dropdown
 * - Date range picker
 * - Clear filters button
 */

import React, { useCallback } from 'react';
import { DateRangeSelector } from '@components/DateRangeSelector/DateRangeSelector';
import PatientPicker from '@components/Patient/PatientPicker';
import { Clear as ClearIcon, FilterList as FilterListIcon } from '@mui/icons-material';
import {
  Box,
  Button,
  Chip,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  type SelectChangeEvent,
  Stack,
} from '@mui/material';
import type { Dayjs } from 'dayjs';

import type { Patient } from '../../../types/types';
import type { DocumentFiltersState } from '../hooks/useDocumentsList';

// Document type options based on API docs
export const DOCUMENT_TYPE_OPTIONS = [
  { value: 'lab_order', label: 'Lab Order' },
  { value: 'form', label: 'Form' },
  { value: 'document', label: 'Document' },
] as const;

interface DocumentFiltersProps {
  filters: DocumentFiltersState;
  onPatientIdChange: (patientId: string | null) => void;
  onDocumentTypeChange: (value: string | null) => void;
  onStartDateChange: (date: Dayjs | null) => void;
  onEndDateChange: (date: Dayjs | null) => void;
  onClearFilters: () => void;
  hasActiveFilters: boolean;
  isLoading?: boolean;
  /** Hide document type filter (used when document type is fixed) */
  hideDocumentTypeFilter?: boolean;
}

export const DocumentFilters: React.FC<DocumentFiltersProps> = ({
  filters,
  onPatientIdChange,
  onDocumentTypeChange,
  onStartDateChange,
  onEndDateChange,
  onClearFilters,
  hasActiveFilters,
  isLoading = false,
  hideDocumentTypeFilter = false,
}) => {
  const handlePatientSelect = useCallback(
    (patient: Patient | undefined) => {
      onPatientIdChange(patient?.patient_id || null);
    },
    [onPatientIdChange],
  );

  const handleDocumentTypeChange = useCallback(
    (event: SelectChangeEvent) => {
      const value = event.target.value;
      onDocumentTypeChange(value === '' ? null : value);
    },
    [onDocumentTypeChange],
  );

  const handleDateRangeChange = useCallback(
    (range: { start: Date | null; end: Date | null }) => {
      // Convert Date to Dayjs or null
      const { default: dayjs } = require('dayjs');
      onStartDateChange(range.start ? dayjs(range.start) : null);
      onEndDateChange(range.end ? dayjs(range.end) : null);
    },
    [onStartDateChange, onEndDateChange],
  );

  // Note: PatientPicker will start empty - if we need to preserve selected patient across refreshes,
  // we would need to fetch the patient data using the patientId. For now, starting empty is fine.

  // Count active filters for the badge (exclude documentType if filter is hidden)
  const activeFilterCount = [
    filters.patientId,
    !hideDocumentTypeFilter && filters.documentType,
    filters.startDate || filters.endDate,
  ].filter(Boolean).length;

  return (
    <Box
      sx={{
        p: 2,
        bgcolor: 'background.paper',
        borderRadius: 2,
        border: 1,
        borderColor: 'divider',
      }}
    >
      <Stack
        direction={{ xs: 'column', md: 'row' }}
        spacing={2} sx={{ alignItems: { xs: 'stretch', md: 'center' }, flexWrap: "wrap" }}
      >
        {/* Patient Picker Filter */}
        <Box sx={{ minWidth: 250, flex: { xs: 1, md: 'none' } }}>
          <PatientPicker onPatientSelect={handlePatientSelect} size="small" />
        </Box>

        {/* Document Type Filter */}
        {!hideDocumentTypeFilter && (
          <FormControl size="small" sx={{ minWidth: 150 }}>
            <InputLabel id="document-type-label">Type</InputLabel>
            <Select
              labelId="document-type-label"
              label="Type"
              value={filters.documentType || ''}
              onChange={handleDocumentTypeChange}
              disabled={isLoading}
            >
              <MenuItem value="">
                <em>All Types</em>
              </MenuItem>
              {DOCUMENT_TYPE_OPTIONS.map(option => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        )}

        {/* Date Range Filter */}
        <Box sx={{ minWidth: 200 }}>
          <DateRangeSelector
            value={{
              start: filters.startDate?.toDate() ?? null,
              end: filters.endDate?.toDate() ?? null,
            }}
            onChange={handleDateRangeChange}
            label="Date Range"
            size="small"
            disabled={isLoading}
          />
        </Box>

        {/* Filter Status & Clear Button */}
        <Stack direction="row" spacing={1} sx={{alignItems: "center",  ml: { md: 'auto' } }}>
          {activeFilterCount > 0 && (
            <Chip
              icon={<FilterListIcon fontSize="small" />}
              label={`${activeFilterCount} filter${activeFilterCount > 1 ? 's' : ''}`}
              size="small"
              color="primary"
              variant="outlined"
            />
          )}

          {hasActiveFilters && (
            <Button
              variant="text"
              size="small"
              onClick={onClearFilters}
              startIcon={<ClearIcon />}
              disabled={isLoading}
            >
              Clear All
            </Button>
          )}
        </Stack>
      </Stack>
    </Box>
  );
};

export default DocumentFilters;
